import React, {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CalendarIcon from "@mui/icons-material/CalendarMonthOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { toast } from "react-toastify";
import NumberField from "../../components/number-field";
import AutocompleteWithPagination from "../../components/autocomplete-with-pagination";
import { inventoryService } from "../../utils/services/inventoryService";
import { productService } from "../../utils/services/productService";
import { categoryService } from "../../utils/services/categoryService";
import { distinctValuesService } from "../../utils/services/distinct-values.service";
import { wmsService } from "../../utils/services/wmsProvider";
import { channelInventoryService } from "../../utils/services/channelInventoryService";
import { clientConfigService } from "../../utils/services/clientConfigService";
import { formatDateTime } from "../../utils/formatDate";
import { fieldSx } from "../manage-catalog/constants";
import { LOST_SALES_SAFE_PERIOD_OPTIONS } from "../../utils/lostSalesParams";
import { useMetricsUi, SectionHead } from "../../components/metrics/metricsUi";
// Unchanged from the classic ../planning-forecast — shared from there rather
// than duplicated.
import { StatCard, cardGridSx } from "../planning-forecast/forecastUi";

// Sales history window (Updated Forecast): at most 36 months back, from the
// first whole month; must end at least one complete month before this one.
const HISTORY_MAX_MONTHS = 36;
const SEASONAL_MIN_MONTHS = 24;
const monthKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const earliestHistoryMonth = (now = new Date()) => {
  const d = new Date(now.getFullYear(), now.getMonth() - HISTORY_MAX_MONTHS, 1);
  // The 36-month limit falls mid-month unless today is the 1st → next month.
  if (now.getDate() > 1) d.setMonth(d.getMonth() + 1);
  return monthKey(d);
};
const latestHistoryMonth = (now = new Date()) =>
  monthKey(new Date(now.getFullYear(), now.getMonth() - 1, 1));
const monthsOfHistory = (fromMonth, now = new Date()) => {
  const [y, m] = String(fromMonth).split("-").map(Number);
  return (now.getFullYear() - y) * 12 + (now.getMonth() + 1 - m);
};

const monthDate = (key) => {
  const [y, m] = String(key).split("-").map(Number);
  return new Date(y, m - 1, 1);
};
const monthLabel = (key) =>
  monthDate(key).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });

/**
 * Where to show a field's calendar popover (position: fixed). Below the field
 * when it fits, else above it; kept inside the window, and scrollable inside
 * itself when the window is shorter than the calendar. Follows the field when
 * the page or any panel scrolls, and on resize.
 */
const POPOVER_GAP = 4;
const VIEWPORT_MARGIN = 8;
export const placePopover = (rect, size, viewport = { width: window.innerWidth, height: window.innerHeight }) => {
  const { width, height } = size;
  const room = viewport.height - 2 * VIEWPORT_MARGIN;
  const below = viewport.height - rect.bottom - POPOVER_GAP - VIEWPORT_MARGIN;
  const above = rect.top - POPOVER_GAP - VIEWPORT_MARGIN;
  let top;
  if (height <= below) top = rect.bottom + POPOVER_GAP;
  else if (height <= above) top = rect.top - POPOVER_GAP - height;
  else top = VIEWPORT_MARGIN + Math.max(0, room - height);
  const left = Math.min(Math.max(VIEWPORT_MARGIN, rect.left), Math.max(VIEWPORT_MARGIN, viewport.width - width - VIEWPORT_MARGIN));
  return { top, left, maxHeight: room };
};

const useFieldPopover = ({ fieldRef, popRef, disabled, width = 300 }) => {
  const [position, setPosition] = useState(null);
  const place = useCallback(() => {
    if (!fieldRef.current) return;
    const rect = fieldRef.current.getBoundingClientRect();
    // Until it has rendered once, assume a full day-view calendar.
    const height = popRef.current?.offsetHeight || 340;
    setPosition(placePopover(rect, { width, height }));
  }, [fieldRef, popRef, width]);
  const open = () => {
    if (disabled || !fieldRef.current) return;
    place();
  };
  const close = () => setPosition(null);
  const isOpen = Boolean(position);

  // Re-place once the real height is known (month vs day view differ).
  useLayoutEffect(() => {
    if (isOpen) place();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onDown = (event) => {
      if (popRef.current && !popRef.current.contains(event.target) && !fieldRef.current?.contains(event.target)) {
        setPosition(null);
      }
    };
    document.addEventListener("mousedown", onDown);
    // Capture: also scrolls of inner panels, not just the window.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [isOpen, place, fieldRef, popRef]);

  return { position, open, close };
};

const popoverSx = (position) => ({
  position: "fixed",
  top: position.top,
  left: position.left,
  maxHeight: position.maxHeight,
  overflowY: "auto",
  zIndex: 3000,
  bgcolor: "var(--bg-paper)",
  boxShadow: 3,
  borderRadius: 1,
  p: 0.5,
  width: 300,
});

/**
 * Month picker in the platform's date-field style (read-only field +
 * react-calendar popover, as on Manage Catalog), opened on the year view so a
 * whole month is picked. `value` / `onChange` use "YYYY-MM".
 */
export const MonthPickerField = ({
  label,
  value,
  onChange,
  min,
  max,
  disabled,
  endText,
  endTextColor,
  helperText,
  FormHelperTextProps,
}) => {
  const fieldRef = useRef(null);
  const calendarRef = useRef(null);
  const { position, open, close } = useFieldPopover({ fieldRef, popRef: calendarRef, disabled });

  return (
    <Box ref={fieldRef}>
      <TextField
        label={label}
        size="small"
        fullWidth
        value={value ? monthLabel(value) : ""}
        onFocus={open}
        onClick={open}
        disabled={disabled}
        InputLabelProps={{ shrink: true }}
        inputProps={{ readOnly: true, "data-testid": "history-from" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IconButton
                size="small"
                aria-label={`Choose ${label}`}
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (position) close();
                  else open();
                }}
              >
                <CalendarIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
          ...(endText && {
            endAdornment: (
              <InputAdornment position="end">
                <Typography
                  component="span"
                  sx={{
                    fontSize: 12,
                    color: endTextColor || "var(--text-secondary)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {endText}
                </Typography>
              </InputAdornment>
            ),
          }),
        }}
        helperText={helperText}
        FormHelperTextProps={FormHelperTextProps}
        sx={fieldSx}
      />
      {position && (
        <Box
          ref={calendarRef}
          sx={popoverSx(position)}
        >
          <Calendar
            value={value ? monthDate(value) : null}
            defaultView="year"
            maxDetail="year"
            minDate={min ? monthDate(min) : undefined}
            maxDate={max ? monthDate(max) : undefined}
            onChange={(date) => {
              onChange(monthKey(date));
              close();
            }}
            className="custom-calendar"
          />
        </Box>
      )}
    </Box>
  );
};

// ── Event uplift (Updated Forecast) ─────────────────────────────────────────
// A lift the user adds for events the sales history can't teach the model
// (e.g. no sales on Black Friday last year). Applied on top of the tested
// forecast and shown separately on the chart and in the insight.
const dayKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const nthWeekday = (year, month, weekday, n) => {
  const d = new Date(year, month, 1);
  while (d.getDay() !== weekday) d.setDate(d.getDate() + 1);
  d.setDate(d.getDate() + 7 * (n - 1));
  return d;
};
const addDays = (d, k) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + k);
// Each preset's dates in a given year; the next occurrence from today is used.
const EVENT_PRESETS = [
  {
    key: "bfcm",
    name: "Black Friday – Cyber Monday",
    dates: (y) => {
      const friday = addDays(nthWeekday(y, 10, 4, 4), 1); // day after Thanksgiving
      return [friday, addDays(friday, 3)];
    },
  },
  { key: "gifting", name: "Holiday gifting peak", dates: (y) => [new Date(y, 11, 15), new Date(y, 11, 24)] },
  {
    key: "prime",
    name: "Prime Day",
    dates: (y) => {
      const d = nthWeekday(y, 6, 2, 2);
      return [d, addDays(d, 1)];
    },
  },
  { key: "valentine", name: "Valentine's Day", dates: (y) => [new Date(y, 1, 7), new Date(y, 1, 14)] },
  { key: "mother", name: "Mother's Day", dates: (y) => [addDays(nthWeekday(y, 4, 0, 2), -6), nthWeekday(y, 4, 0, 2)] },
  { key: "father", name: "Father's Day", dates: (y) => [addDays(nthWeekday(y, 5, 0, 3), -6), nthWeekday(y, 5, 0, 3)] },
];
const presetDates = (key, now = new Date()) => {
  const preset = EVENT_PRESETS.find((p) => p.key === key);
  if (!preset) return null;
  const today = dayKey(now);
  for (const y of [now.getFullYear(), now.getFullYear() + 1]) {
    const [a, b] = preset.dates(y);
    if (dayKey(b) >= today) return { name: preset.name, start: dayKey(a), end: dayKey(b) };
  }
  return null;
};
const newUpliftRow = () => ({ id: Math.random().toString(36).slice(2), preset: "bfcm", ...presetDates("bfcm"), lift_pct: "" });
// Event uplifts the forecast agent drafted → form rows. A known event (by
// `preset` key or its exact name) without dates gets that event's next dates.
export const upliftRowsFromDraft = (list) =>
  (Array.isArray(list) ? list : [])
    .map((u) => {
      const preset =
        EVENT_PRESETS.find((p) => p.key === u?.preset) ||
        EVENT_PRESETS.find((p) => p.name.toLowerCase() === String(u?.name || "").trim().toLowerCase());
      const dates = preset && !u?.start ? presetDates(preset.key) : null;
      const start = String(u?.start || dates?.start || "").slice(0, 10);
      return {
        id: Math.random().toString(36).slice(2),
        preset: preset ? preset.key : "custom",
        name: String(u?.name || preset?.name || "").trim(),
        start,
        end: String(u?.end || dates?.end || start).slice(0, 10),
        lift_pct: u?.lift_pct == null ? "" : String(u.lift_pct),
      };
    })
    .slice(0, 20);

const upliftRowProblem = (r) => {
  if (!r.name?.trim()) return "Give the event a name.";
  if (!r.start) return `"${r.name}" needs a start date.`;
  if (r.end && r.end < r.start) return `"${r.name}" ends before it starts.`;
  const lift = Number(r.lift_pct);
  if (r.lift_pct === "" || !Number.isFinite(lift) || lift === 0) return `Enter the expected lift % for "${r.name}".`;
  if (lift < -90 || lift > 1000) return `"${r.name}": the lift must be between -90% and 1000%.`;
  return null;
};

// The days the forecast will cover: from the start of the current period
// (the service forecasts the current, unfinished period first) for
// `horizon` periods. → { start, end } as "YYYY-MM-DD", end inclusive.
export const forecastWindow = (bucket, horizon, now = new Date()) => {
  const h = Math.max(1, Number(horizon) || 1);
  const b = String(bucket || "Monthly").toLowerCase();
  let start;
  let endExclusive;
  if (b === "daily") {
    start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    endExclusive = addDays(start, h);
  } else if (b === "weekly") {
    start = addDays(new Date(now.getFullYear(), now.getMonth(), now.getDate()), -((now.getDay() + 6) % 7));
    endExclusive = addDays(start, 7 * h);
  } else {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
    endExclusive = new Date(now.getFullYear(), now.getMonth() + h, 1);
  }
  return { start: dayKey(start), end: dayKey(addDays(endExclusive, -1)) };
};
const shortDay = (k) =>
  new Date(`${k}T00:00:00`).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });

/** Day picker in the platform's date-field style (read-only field + react-calendar popover). */
export const DatePickerField = ({ label, value, onChange, min, disabled, testId }) => {
  const fieldRef = useRef(null);
  const calendarRef = useRef(null);
  const { position, open, close } = useFieldPopover({ fieldRef, popRef: calendarRef, disabled });
  const asDate = (k) => (k ? new Date(`${k}T00:00:00`) : null);
  return (
    <Box ref={fieldRef}>
      <TextField
        label={label}
        size="small"
        fullWidth
        value={
          value
            ? asDate(value).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" })
            : ""
        }
        onFocus={open}
        onClick={open}
        disabled={disabled}
        InputLabelProps={{ shrink: true }}
        inputProps={{ readOnly: true, "data-testid": testId }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IconButton
                size="small"
                aria-label={`Choose ${label}`}
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (position) close();
                  else open();
                }}
              >
                <CalendarIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={fieldSx}
      />
      {position && (
        <Box
          ref={calendarRef}
          sx={popoverSx(position)}
        >
          <Calendar
            value={asDate(value)}
            minDate={asDate(min) || undefined}
            onChange={(date) => {
              onChange(dayKey(date));
              close();
            }}
            className="custom-calendar"
          />
        </Box>
      )}
    </Box>
  );
};

const parsePositiveIntegerInput = (value) => {
  if (value === "") return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) return null;
  return parsed;
};

const num = (v, dp = 0) =>
  v === null || v === undefined || v === "" || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString("en-US", {
        minimumFractionDigits: dp,
        maximumFractionDigits: dp,
      });

const productName = (p) =>
  !p ? null : typeof p === "string" ? null : p.name || null;

// `input.warehouse` / `input.channel` are multi-select now — a stored run can
// carry several of either (older runs still store a plain scalar). Render
// whatever's there as a comma-joined label.
const warehouseName = (w) =>
  !w ? "" : typeof w === "string" ? w : w.name || w.warehouse_code || "";
const joinedLabel = (value, toLabel = (v) => v) =>
  (Array.isArray(value) ? value : value ? [value] : [])
    .map(toLabel)
    .filter(Boolean)
    .join(", ");

// ── Draft plans from the forecast AI agent ──
// The agent's `draftForecast` tool (see ForecastPlannerAgent) hands back the
// same item shape handleSubmit below builds, so applying a draft is just the
// reverse: turn each item's raw ids/names back into whatever shape each
// picker's `selectedItem` needs to render a label. The agent never submits —
// this only pre-fills the form for a human to review and run themselves.
//
// Read defensively: the agent's actual tool-call args live on an external
// LLM's compliance with the schema we hand it — a near-miss field name (an
// older prompt revision, a model that improvises) shouldn't silently produce
// a blank form, so accept a few plausible aliases per field.
const draftField = (item, ...names) => {
  for (const n of names) {
    const v = item?.[n];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
};

// If the agent didn't send a recognized `forecast_by`, infer it from
// whichever target field the item actually carries rather than defaulting
// blind to SKU.
const normalizeForecastByValue = (rawValue, item) => {
  const v = String(rawValue || "")
    .trim()
    .toLowerCase();
  if (v === "sku") return "SKU";
  if (v === "product") return "Product";
  if (v === "category") return "Category";
  if (v === "product type" || v === "product_type") return "Product Type";
  if (draftField(item, "sku")) return "SKU";
  if (draftField(item, "product", "target_id", "product_id")) return "Product";
  if (draftField(item, "category", "category_name")) return "Category";
  if (draftField(item, "product_type", "productType")) return "Product Type";
  return "SKU";
};

// forecast_type stays "warehouse" unless the agent explicitly says the plan
// is scoped BY channel — never inferred from channel merely being present,
// since channel doubles as an optional filter within a warehouse forecast.
const normalizeForecastType = (rawValue) =>
  String(rawValue || "")
    .trim()
    .toLowerCase() === "channel"
    ? "channel"
    : "warehouse";

const skuItemsFromDraft = (items, by) => {
  if (by === "Category" || by === "Product Type") {
    const first = items[0] || {};
    if (by === "Category") {
      const category = draftField(first, "category", "category_name");
      return category ? [{ name: category }] : [];
    }
    const productType = draftField(first, "product_type", "productType");
    return productType ? [{ product_type: productType }] : [];
  }
  if (by === "Product") {
    // At Product scope the backend already expands one product into every
    // one of its SKU variants — an agent listing the same product once per
    // variant (e.g. "Widget - Small", "Widget - Large") would otherwise
    // forecast that product twice. Dedupe by id, first name wins.
    const seen = new Set();
    const out = [];
    for (const it of items) {
      const _id = draftField(it, "product", "target_id", "product_id");
      if (!_id || seen.has(_id)) continue;
      seen.add(_id);
      out.push({
        _id,
        name: draftField(it, "product_name", "target_name", "name") || _id,
      });
    }
    return out;
  }
  return items
    .map((it) => ({
      sku: draftField(it, "sku"),
      product: draftField(it, "product", "target_id", "product_id"),
    }))
    .filter((it) => it.sku);
};

const warehousesFromDraftItem = (item) => {
  const rawIds = draftField(item, "warehouse", "warehouses", "warehouse_ids");
  const ids = Array.isArray(rawIds) ? rawIds : rawIds ? [rawIds] : [];
  const rawNames = draftField(item, "warehouse_names", "warehouseNames");
  const names = Array.isArray(rawNames) ? rawNames : [];
  return ids
    .filter(Boolean)
    .map((id, i) => ({ _id: id, name: names[i] || id }));
};

const channelsFromDraftItem = (item) => {
  const rawValues = draftField(item, "channel", "channels");
  const values = Array.isArray(rawValues)
    ? rawValues
    : rawValues
      ? [rawValues]
      : [];
  return values.filter(Boolean).map((c) => ({ _id: c, name: c }));
};

/**
 * Parameters section for Planning > Forecast.
 *  - view mode: read-only params of the stored run, "Run at" sits top-right
 *  - edit mode: the panel itself becomes an inline editable form (no modal) —
 *    Forecast By + multi-SKU picker + time bucket + periods + lost sales +
 *    promo discount + warehouse(s) + channel(s). Warehouse/channel are both
 *    multi-select; picking several fans out into one forecast run per
 *    warehouse × channel combination (mirrors the SKU/category expansion the
 *    backend already does). Engine is not exposed and stays "kaman".
 *
 * Exposes a ref handle ({ runForecast, canRunForecast }) so a host can
 * trigger the exact same submit path as clicking "Run Forecast" — used by
 * the forecast agent's `runForecast` uiTool (see ForecastPlannerAgent),
 * kept entirely separate from `draftForecast`'s form-filling behavior.
 */
// Client config key for the default lead time (see control-tower default-client-config).
const LEAD_TIME_CONFIG_KEY = "forecast_lead_time_days";
const DEFAULT_LEAD_TIME_DAYS = 21;

const ForecastParamsPanel = forwardRef(function ForecastParamsPanel(
  {
    run,
    clientId,
    editing,
    // A plan the forecast AI agent drafted (see pages/forecast's
    // `draftForecast` tool) — applied once when edit mode opens, then the
    // parent should clear it via `onDraftConsumed` so it isn't re-applied.
    draft,
    onDraftConsumed,
    onCancel,
    onForecasted,
    onSubmittingChange,
    // True when the active run/selection represents the whole product
    // ("All SKUs"), not one specific SKU — the SKU field is hidden in
    // that case since there's no single SKU to show.
    isAllSkusView,
    // "kaman" (default) or "demand_forecast" — the admin Updated Forecast
    // page. Same inputs either way; only the engine that runs them differs.
    engine = "kaman",
    // Client users: Sales Fill is shown but can't be turned on (admin only).
    salesFillLocked = false,
    // Updated Forecast page: the run's Summary + Method cards, shown inside
    // this panel so the Insight and chart sit right under it.
    extra = null,
    // Start the read-only view collapsed (Updated Forecast page).
    defaultCollapsed = false,
    // One-line headline shown in the header while collapsed.
    collapsedHint = "",
  },
  ref,
) {
  const ui = useMetricsUi();
  const { colors } = ui;
  const input = run?.input || {};

  const [forecastBy, setForecastBy] = useState("SKU");
  const [skuItems, setSkuItems] = useState([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);
  // "warehouse" (default) or "channel" — only the agent draft sets this to
  // "channel", when the user explicitly asked to forecast BY channel rather
  // than just filtering a warehouse forecast down to some channels.
  const [forecastType, setForecastType] = useState("warehouse");
  const [horizonType, setHorizonType] = useState("Monthly");
  const [horizon, setHorizon] = useState(6);

  // Supply-planning overrides — sent with every forecast item.
  const [leadTimeDays, setLeadTimeDays] = useState(DEFAULT_LEAD_TIME_DAYS);
  const [safetyStockPct, setSafetyStockPct] = useState(10); // % of on hand
  // Extra days of stock the reorder must cover beyond the lead time.
  const [minCoverageDays, setMinCoverageDays] = useState(0);
  // Floor for each SKU's order; blank = the SKU's own inventory-config MOQ.
  const [minOrderQty, setMinOrderQty] = useState("");

  const isUpdatedEngine = engine === "demand_forecast";
  // Updated Forecast lost sales are worked out across the warehouse network.
  const networkLostSales = isUpdatedEngine;
  // Sales history start (Updated Forecast only), "YYYY-MM".
  const [historyFrom, setHistoryFrom] = useState(() => earliestHistoryMonth());
  // Event uplifts (Updated Forecast only).
  const [eventUplifts, setEventUplifts] = useState([]);
  const [upliftCollapsed, setUpliftCollapsed] = useState(true);
  const upliftProblem = eventUplifts.map(upliftRowProblem).find(Boolean) || null;
  const window_ = forecastWindow(horizonType, horizon);
  // Rows whose dates miss the forecast entirely add nothing — say so up front.
  const upliftOutside = (r) =>
    Boolean(r.start) && ((r.end || r.start) < window_.start || r.start > window_.end);
  const updateUplift = (id, patch) =>
    setEventUplifts((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  // Off by default on every engine; the user turns it on per run.
  const [includeLostSales, setIncludeLostSales] = useState(false);
  // Sales fill (Updated Forecast): estimate the periods before a SKU's first sale.
  const [salesFill, setSalesFill] = useState(false);
  const [searchWindowDays, setSearchWindowDays] = useState(180);
  const [demandWindowDays, setDemandWindowDays] = useState(null);
  const [dowAdjustmentEnabled, setDowAdjustmentEnabled] = useState(true);

  const [discountCollapsed, setDiscountCollapsed] = useState(true);
  const [discountRange, setDiscountRange] = useState("");
  const [discountValue, setDiscountValue] = useState(0);

  const [submitting, setSubmitting] = useState(false);

  // Read-only "Forecast Parameters" view — collapsible like Explanation, but
  // open by default since these are the primary at-a-glance values.
  const [paramsOpen, setParamsOpen] = useState(!defaultCollapsed);
  // Per-SKU forecast results (shown after a run when any SKU failed)
  const [resultDialog, setResultDialog] = useState({ open: false, items: [] });

  // Starting a fresh edit (New / Custom) resets the form to defaults — but
  // ONLY if no agent draft is already queued at that exact moment (a draft
  // that lands in the same beat `editing` turns true shouldn't be blown
  // away). This intentionally depends on `editing` alone, not `draft` — see
  // the effect below for why.
  useEffect(() => {
    if (!editing) return;
    setSubmitting(false);
    onSubmittingChange?.(false);
    if (draft) return;
    setForecastBy("SKU");
    setSkuItems([]);
    setSelectedWarehouses([]);
    setSelectedChannels([]);
    setForecastType("warehouse");
    setHorizonType("Monthly");
    setHorizon(6);
    setLeadTimeDays(DEFAULT_LEAD_TIME_DAYS);
    setSafetyStockPct(10);
    setMinCoverageDays(0);
    setMinOrderQty("");
    setIncludeLostSales(false);
    setSalesFill(false);
    setHistoryFrom(earliestHistoryMonth());
    setEventUplifts([]);
    setUpliftCollapsed(true);
    setSearchWindowDays(180);
    setDemandWindowDays(null);
    setDowAdjustmentEnabled(true);
    setDiscountValue(0);
    setDiscountRange("");
    setDiscountCollapsed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  // Lead time comes from the client's config (Client Config → "Forecast Lead
  // Time (days)"), default 21. Applied when a fresh form opens, and only if
  // the field still holds the default — never over a value typed or drafted.
  useEffect(() => {
    if (!editing || draft || !clientId) return undefined;
    let cancelled = false;
    clientConfigService
      .fetch(clientId, 1, 0, LEAD_TIME_CONFIG_KEY)
      .then((res) => {
        const v = Number((res?.data || res || [])[0]?.value);
        if (!cancelled && Number.isFinite(v) && v >= 0) {
          setLeadTimeDays((cur) => (cur === DEFAULT_LEAD_TIME_DAYS ? v : cur));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, clientId]);

  // Apply a plan the forecast AI agent drafted — fires whenever a new draft
  // arrives while the form is open, including a second/updated draft later
  // in the same conversation (the agent can call draftForecast more than
  // once as the plan evolves). Crucially this effect never resets the form
  // on its own: after applying a draft we tell the parent to clear it
  // (`onDraftConsumed`), which flips `draft` back to null and re-runs this
  // effect — but the guard below just no-ops on a falsy draft instead of
  // wiping out what was just populated (that was the bug: a single combined
  // effect keyed on `[editing, draft]` treated "draft is now null" the same
  // as "start a fresh blank form" and reset immediately after populating).
  useEffect(() => {
    if (!editing || !draft) return;
    const first = draft.items?.[0] || {};
    const by = normalizeForecastByValue(first.forecast_by, first);
    const draftDiscount =
      Number(draftField(first, "discount", "promotional_discount_percent")) ||
      0;
    setForecastBy(by);
    setSkuItems(skuItemsFromDraft(draft.items || [], by));
    setSelectedWarehouses(warehousesFromDraftItem(first));
    setSelectedChannels(channelsFromDraftItem(first));
    setForecastType(normalizeForecastType(draft.forecast_type));
    setHorizonType(draftField(first, "type", "time_bucket") || "Monthly");
    setHorizon(
      draftField(first, "horizon", "periods", "number_of_periods") ?? 6,
    );
    setLeadTimeDays(
      draftField(first, "lead_time_days") ?? DEFAULT_LEAD_TIME_DAYS,
    );
    setSafetyStockPct(
      draftField(first, "safety_stock_percentage", "safety_stock_percent") ??
        10,
    );
    setMinCoverageDays(draftField(first, "min_coverage_days") ?? 0);
    setMinOrderQty(draftField(first, "min_order_qty", "moq") ?? "");
    setIncludeLostSales(!!draft.include_lost_sales);
    setSalesFill(!salesFillLocked && !!draft.sales_fill);
    setSearchWindowDays(draft.search_window_days ?? 180);
    setDemandWindowDays(draft.demand_window_days ?? null);
    setDowAdjustmentEnabled(draft.dow_adjustment_enabled !== false);
    setDiscountValue(draftDiscount);
    setDiscountRange(draftDiscount > 0 ? "custom" : "");
    setDiscountCollapsed(draftDiscount <= 0);
    // Updated Forecast only: Sales History From and Event Uplift.
    if (isUpdatedEngine) {
      const from = String(draft.history_start || "").slice(0, 7);
      if (/^\d{4}-\d{2}$/.test(from)) {
        // Kept inside the range the field allows (36 months back … last month).
        const lo = earliestHistoryMonth();
        const hi = latestHistoryMonth();
        setHistoryFrom(from < lo ? lo : from > hi ? hi : from);
      }
      const uplifts = upliftRowsFromDraft(draft.event_uplifts);
      setEventUplifts(uplifts);
      setUpliftCollapsed(uplifts.length === 0);
    }
    setSubmitting(false);
    onSubmittingChange?.(false);
    onDraftConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, draft]);

  const nearestSafePeriodActive = demandWindowDays != null;
  const invalidSearchWindow =
    includeLostSales &&
    (!Number.isInteger(searchWindowDays) || searchWindowDays <= 0);
  const invalidDemandWindow =
    includeLostSales &&
    nearestSafePeriodActive &&
    (!Number.isInteger(demandWindowDays) || demandWindowDays <= 0);
  const demandExceedsSearch =
    includeLostSales &&
    nearestSafePeriodActive &&
    demandWindowDays > searchWindowDays;
  const invalidLostSales =
    invalidSearchWindow || invalidDemandWindow || demandExceedsSearch;

  const discount = useMemo(() => {
    const n = Number(discountValue);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [discountValue]);

  // Multi-select — picking several warehouses/channels fans out into one
  // forecast run per combination (handled server-side).
  const warehouseIds = useMemo(
    () => selectedWarehouses.map((w) => w?._id || w).filter(Boolean),
    [selectedWarehouses],
  );
  const channelValues = useMemo(
    () => selectedChannels.map((c) => c?.name || c?._id || c).filter(Boolean),
    [selectedChannels],
  );

  const byProduct = forecastBy === "Product";
  const byCategory = forecastBy === "Category";
  const byProductType = forecastBy === "Product Type";
  // Category / Product Type pick a group; on submit it's expanded into the
  // products it contains, each forecast at product level.
  const byGroup = byCategory || byProductType;

  const canSubmit =
    skuItems.length > 0 &&
    skuItems.every((it) =>
      byGroup || byProduct
        ? it?._id || it?.product || it?.name || it?.product_type
        : (it?.variants?.sku || it?.sku) && (it?._id || it?.product),
    ) &&
    Number(horizon) > 0 &&
    !invalidLostSales &&
    !upliftProblem &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) {
      return {
        success: false,
        message: submitting
          ? "A forecast run is already in progress."
          : upliftProblem ||
            "The form isn't ready to run yet — check the required fields (target, horizon, lost-sales settings).",
      };
    }
    setSubmitting(true);
    onSubmittingChange?.(true);
    try {
      // Supply-planning overrides carried on every forecast item.
      const supplyOverrides = {
        lead_time_days: Number(leadTimeDays) || 0,
        safety_stock_percentage: Number(safetyStockPct) || 0,
        min_coverage_days: Number(minCoverageDays) || 0,
        ...(minOrderQty !== "" && minOrderQty != null
          ? { min_order_qty: Number(minOrderQty) || 0 }
          : {}),
      };

      // Same request-object shape as the inventory screen's forecast call —
      // no extra keys beyond what it sends.
      let items;
      if (byGroup) {
        // One item per selected group value, using inventory's keys:
        // `category` (name) or `product_type`. The backend pulls every SKU
        // under those values and runs a normal SKU-level bulk forecast.
        items = skuItems.map((grp) => ({
          ...(byCategory
            ? { category: grp.name }
            : { product_type: grp.product_type || grp.name }),
          horizon: Number(horizon),
          type: horizonType,
          ...(warehouseIds.length ? { warehouse: warehouseIds } : {}),
          ...(channelValues.length ? { channel: channelValues } : {}),
          discount,
          forecast_by: byCategory ? "category" : "product type",
          client: clientId,
          ...supplyOverrides,
        }));
      } else {
        // SKU mode: items come from the product SKU API (/fetch-sku). Product
        // mode: items come from the product name search (/product); the backend
        // expands each product into its variants. Warehouse(s)/channel(s) come
        // from the dropdowns — arrays fan out into one run per combination.
        items = skuItems.map((item) => ({
          sku: byProduct ? undefined : item.variants?.sku || item.sku,
          product: item._id || item.product,
          category: item.category,
          product_type: item.product_type,
          horizon: Number(horizon),
          type: horizonType,
          ...(warehouseIds.length ? { warehouse: warehouseIds } : {}),
          ...(channelValues.length ? { channel: channelValues } : {}),
          discount,
          forecast_by: forecastBy,
          client: item.client_id || item.client || clientId,
          ...supplyOverrides,
        }));
      }

      const payload = {
        items,
        bulk: true,
        forecast_type: forecastType,
        engine,
        ...(isUpdatedEngine && historyFrom && { history_start: historyFrom }),
        ...(isUpdatedEngine && salesFill && !salesFillLocked && { sales_fill: true }),
        ...(isUpdatedEngine &&
          eventUplifts.length && {
            event_uplifts: eventUplifts.map((r) => ({
              name: r.name.trim(),
              start: r.start,
              end: r.end || r.start,
              lift_pct: Number(r.lift_pct),
            })),
          }),
        ...(includeLostSales && {
          include_lost_sales: true,
          search_window_days: searchWindowDays,
          demand_window_days: demandWindowDays,
          dow_adjustment_enabled: dowAdjustmentEnabled,
        }),
      };
      const res = await inventoryService.bulkForecast(payload);

      const results = Array.isArray(res) ? res : [];
      const successes = results.filter(
        (r) => !r.error && r.data?.forecast_data_id,
      );
      const failures = results.filter((r) => r.error);

      // Any failure → show the per-SKU results dialog (same as the inventory
      // forecast). Successes still flow through so their tabs render.
      if (failures.length) {
        setResultDialog({ open: true, items: results });
      }

      if (successes.length) {
        if (!failures.length) {
          toast.success(`${successes.length} forecast(s) generated`);
        }
        onForecasted?.(successes.map((s) => s.data.forecast_data_id));
        return {
          success: true,
          message: failures.length
            ? `${successes.length} of ${results.length} forecast(s) generated; ${failures.length} failed.`
            : `${successes.length} forecast(s) generated.`,
          ids: successes.map((s) => s.data.forecast_data_id),
        };
      } else if (!failures.length) {
        toast.error("Unable to generate forecast now. Please try again later.");
        return {
          success: false,
          message: "Unable to generate forecast now. Please try again later.",
        };
      }
      return {
        success: false,
        message: `${failures.length} forecast(s) failed.`,
      };
    } catch (e) {
      console.error("Forecast run failed", e);
      const message =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to request forecast";
      toast.error(message);
      return { success: false, message };
    } finally {
      setSubmitting(false);
      onSubmittingChange?.(false);
    }
  };

  // Exposed to the host so the forecast agent's `runForecast` uiTool can
  // trigger the exact same submit path as clicking "Run Forecast" — no
  // separate submission logic, no bearing on `draftForecast` above.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(
    ref,
    () => ({
      runForecast: () => handleSubmit(),
      canRunForecast: () => canSubmit,
    }),
    [canSubmit, handleSubmit],
  );

  const pName = productName(input.product);

  const blockSx = {
    border: `1px solid ${colors.border}`,
    borderRadius: "10px",
    backgroundColor: ui.surfaceAlt,
    p: 1.75,
  };

  const runAt = run && (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Box sx={{ textAlign: "right" }}>
        <Typography
          sx={{
            fontSize: 10.5,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: colors.textPrimary,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          Run at : {formatDateTime(run.createdAt)}
        </Typography>
      </Box>
      <Button
        size="small"
        onClick={() => setParamsOpen((o) => !o)}
        endIcon={paramsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{
          textTransform: "none",
          color: colors.textSecondary,
          fontSize: 12,
          minHeight: 0,
          minWidth: 0,
          lineHeight: 1.4,
          py: 0.25,
          px: 1,
          "& .MuiButton-endIcon": { ml: 0.3 },
          "& .MuiButton-endIcon > svg": { fontSize: 16 },
        }}
      >
        {paramsOpen ? "Hide" : "Show"}
      </Button>
    </Box>
  );

  const actions = (
    <Box sx={{ display: "flex", gap: 1 }}>
      <Button size="small" onClick={onCancel} disabled={submitting}>
        Cancel
      </Button>
      <Button
        size="small"
        variant="contained"
        color="primary"
        startIcon={<AutorenewIcon />}
        onClick={handleSubmit}
        disabled={!canSubmit}
      >
        {submitting ? "Running…" : "Run Forecast"}
      </Button>
    </Box>
  );

  const results = resultDialog.items;
  const failCount = results.filter((r) => r.error).length;
  const okCount = results.length - failCount;

  return (
    <>
      <Box
        sx={{ ...ui.card, p: { xs: "16px", sm: "18px 20px" } }}
        component="section"
      >
        <SectionHead
          title={editing ? "Edit Parameters" : "Forecast Parameters"}
          subtitle={
            editing
              ? byGroup
                ? `Every SKU in the selected ${byCategory ? "category" : "product type"} is forecast in the background.`
                : byProduct
                  ? "Each selected product is expanded into its SKUs."
                  : "A separate forecast run is generated for each selected SKU."
              : !paramsOpen && collapsedHint
                ? collapsedHint
                : undefined
          }
          right={editing ? actions : runAt}
          sx={{ mb: editing || paramsOpen ? 1.25 : 0 }}
        />

        {editing ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.75,
              mt: 0.5,
            }}
          >
            {/* Line 1 — Forecast By | Time Bucket | Number of Periods
                (+ Sales History From on the Updated Forecast, as 2 × 2) */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: isUpdatedEngine ? "repeat(2, 1fr)" : "repeat(3, 1fr)",
                },
                alignItems: "start",
                gap: 1.75,
              }}
            >
              <TextField
                select
                fullWidth
                size="small"
                label="Forecast By"
                value={forecastBy}
                onChange={(e) => {
                  setForecastBy(e.target.value);
                  setSkuItems([]);
                }}
                disabled={submitting}
                sx={fieldSx}
              >
                <MenuItem value="SKU">SKU</MenuItem>
                <MenuItem value="Product">Product</MenuItem>
                <MenuItem value="Category">Category</MenuItem>
                <MenuItem value="Product Type">Product Type</MenuItem>
              </TextField>

              <TextField
                select
                fullWidth
                size="small"
                label="Time Bucket"
                value={horizonType}
                onChange={(e) => setHorizonType(e.target.value)}
                disabled={submitting}
                sx={fieldSx}
              >
                <MenuItem value="Daily">Daily</MenuItem>
                <MenuItem value="Weekly">Weekly</MenuItem>
                <MenuItem value="Monthly">Monthly</MenuItem>
              </TextField>

              <NumberField
                label="Number of Periods"
                value={horizon}
                onChange={(val) => setHorizon(val)}
                fullWidth
                size="small"
                disabled={submitting}
                placeholder="Enter number of periods"
                style={{ backgroundColor: "var(--bg-paper)" }}
              />
              {isUpdatedEngine && (
                <MonthPickerField
                  label="Sales History From"
                  value={historyFrom}
                  onChange={(v) => {
                    const min = earliestHistoryMonth();
                    const max = latestHistoryMonth();
                    setHistoryFrom(v < min ? min : v > max ? max : v);
                  }}
                  min={earliestHistoryMonth()}
                  max={latestHistoryMonth()}
                  disabled={submitting}
                  endText={
                    monthsOfHistory(historyFrom) < SEASONAL_MIN_MONTHS
                      ? `${monthsOfHistory(historyFrom)} months · under 2 yrs`
                      : `${monthsOfHistory(historyFrom)} months of sales (max ${HISTORY_MAX_MONTHS})`
                  }
                  endTextColor={
                    monthsOfHistory(historyFrom) < SEASONAL_MIN_MONTHS
                      ? ui.warning
                      : undefined
                  }
                />
              )}
            </Box>

            {/* Line 2 — SKU / Product / Category / Product Type */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 1.75,
              }}
            >
              <AutocompleteWithPagination
                key={forecastBy}
                multiple={!byGroup}
                selectedItem={byGroup ? skuItems[0] || null : skuItems}
                onDataChange={(val) =>
                  setSkuItems(
                    byGroup
                      ? val
                        ? [val]
                        : []
                      : Array.isArray(val)
                        ? val
                        : [],
                  )
                }
                customLabel={
                  byCategory
                    ? "Category"
                    : byProductType
                      ? "Product Type"
                      : byProduct
                        ? "Products (search by name)"
                        : "SKUs"
                }
                optionKey={(item) =>
                  byProductType
                    ? item?.product_type || ""
                    : byCategory || byProduct
                      ? item?.name || ""
                      : item?.variants?.sku || item?.sku || ""
                }
                apiEndPoint={(limit, skip, search) => {
                  if (byCategory)
                    return categoryService.fetchNames(
                      search,
                      clientId,
                      limit || 15,
                      skip || 0,
                    );
                  if (byProductType)
                    return distinctValuesService.fetchColumnValues(
                      "product",
                      "product_type",
                      search,
                      clientId,
                      limit || 15,
                      skip || 0,
                    );
                  if (byProduct)
                    return productService.findAll(
                      clientId,
                      search,
                      `$limit=${limit || 15}&$skip=${skip || 0}`,
                    );
                  return productService.fetchSKU(
                    clientId,
                    search,
                    limit,
                    skip,
                    [],
                    null,
                  );
                }}
                disabled={submitting}
                width="100%"
                inputSx={fieldSx}
              />
            </Box>

            {/* Line 3 — Warehouse(s) | Channel(s), both optional & multi-select.
              Picking several of either fans out into one forecast run per
              warehouse × channel combination (handled server-side). */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                gap: 1.75,
              }}
            >
              <AutocompleteWithPagination
                multiple
                selectedItem={selectedWarehouses}
                onDataChange={(val) =>
                  setSelectedWarehouses(Array.isArray(val) ? val : [])
                }
                customLabel="Warehouses (optional)"
                optionKey={(item) =>
                  item?.name || item?.warehouse_code || item?.code || ""
                }
                apiEndPoint={(limit, skip, search) =>
                  wmsService.fetch(clientId, limit, skip, undefined, search)
                }
                disabled={submitting}
                width="100%"
                inputSx={fieldSx}
              />

              <AutocompleteWithPagination
                multiple
                selectedItem={selectedChannels}
                onDataChange={(val) =>
                  setSelectedChannels(Array.isArray(val) ? val : [])
                }
                customLabel="Channels (optional)"
                optionKey="name"
                apiEndPoint={(limit, skip, search) =>
                  channelInventoryService.fetchSourceChannels(
                    search,
                    clientId,
                    limit || 15,
                    skip || 0,
                  )
                }
                disabled={submitting}
                width="100%"
                inputSx={fieldSx}
              />
            </Box>

            {/* Line 4 — Lead Time | Safety Stock (% of on hand) */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                gap: 1.75,
              }}
            >
              <NumberField
                label="Lead Time (days)"
                value={leadTimeDays}
                onChange={(val) =>
                  setLeadTimeDays(val === "" ? "" : Math.max(0, Number(val)))
                }
                inputProps={{ min: 0, step: 1 }}
                fullWidth
                size="small"
                disabled={submitting}
                placeholder="Enter lead time in days"
                style={{ backgroundColor: "var(--bg-paper)" }}
              />
              <NumberField
                label="Safety Stock (% of on hand)"
                value={safetyStockPct}
                onChange={(val) =>
                  setSafetyStockPct(val === "" ? "" : Math.max(0, Number(val)))
                }
                inputProps={{ min: 0, step: 1 }}
                fullWidth
                size="small"
                disabled={submitting}
                placeholder="Enter safety stock %"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ fontWeight: 600 }}>
                      %
                    </InputAdornment>
                  ),
                }}
                style={{ backgroundColor: "var(--bg-paper)" }}
              />
            </Box>

            {/* Line 5 — Minimum coverage window | Minimum order quantity */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                gap: 1.75,
              }}
            >
              <NumberField
                label="Minimum Coverage (days)"
                value={minCoverageDays}
                onChange={(val) =>
                  setMinCoverageDays(val === "" ? "" : Math.max(0, Number(val)))
                }
                inputProps={{ min: 0, step: 1 }}
                fullWidth
                size="small"
                disabled={submitting}
                placeholder="Extra days of stock beyond lead time"
                style={{ backgroundColor: "var(--bg-paper)" }}
              />
              <NumberField
                label="Min Order Qty (per SKU)"
                value={minOrderQty}
                onChange={(val) =>
                  setMinOrderQty(val === "" ? "" : Math.max(0, Number(val)))
                }
                inputProps={{ min: 0, step: 1 }}
                fullWidth
                size="small"
                disabled={submitting}
                placeholder="Blank = each SKU's own MOQ"
                style={{ backgroundColor: "var(--bg-paper)" }}
              />
            </Box>

            {/* Lost Sales */}
            <Box
              sx={{
                ...blockSx,
                p: 0,
                overflow: "hidden",
                borderColor: includeLostSales ? ui.accent : colors.border,
                transition: "border-color 0.2s",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  p: 1.75,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: 13,
                      color: colors.textPrimary,
                    }}
                  >
                    Include Lost Sales
                  </Typography>
                  <Typography
                    sx={{ fontSize: 12, color: colors.textSecondary, mt: 0.25 }}
                  >
                    Adds back the sales missed while the SKU was out of stock.
                    {networkLostSales &&
                      (selectedWarehouses.length
                        ? ` (Across the ${selectedWarehouses.length} selected warehouse${selectedWarehouses.length > 1 ? "s" : ""})`
                        : " (Across your Analytics Warehouses (or all active ones))")}
                  </Typography>
                </Box>
                <Switch
                  checked={includeLostSales}
                  onChange={(e) => setIncludeLostSales(e.target.checked)}
                  disabled={submitting}
                  inputProps={{ "aria-label": "Include Lost Sales" }}
                  sx={{ mr: -0.75 }}
                />
              </Box>
              <Collapse in={includeLostSales} unmountOnExit>
                <Box
                  sx={{
                    borderTop: `1px solid ${colors.border}`,
                    backgroundColor: colors.bgPaper,
                    p: 1.75,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.75,
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 1.75,
                      alignItems: "start",
                    }}
                  >
                    <TextField
                      label="Search Window"
                      type="number"
                      size="small"
                      value={searchWindowDays}
                      onChange={(e) => {
                        const parsed = parsePositiveIntegerInput(
                          e.target.value,
                        );
                        if (parsed !== null) setSearchWindowDays(parsed);
                      }}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography
                              component="span"
                              sx={{
                                fontSize: 12,
                                color: colors.textSecondary,
                                whiteSpace: "nowrap",
                              }}
                            >
                              days back for normal sales
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{ min: 1, step: 1 }}
                      fullWidth
                      disabled={submitting}
                      error={invalidSearchWindow}
                      helperText={
                        invalidSearchWindow
                          ? "Enter a positive whole number of days."
                          : undefined
                      }
                      sx={fieldSx}
                    />
                    <FormControl
                      fullWidth
                      size="small"
                      disabled={submitting}
                      error={invalidDemandWindow || demandExceedsSearch}
                      sx={fieldSx}
                    >
                      <InputLabel id="pf-safe-period-label" shrink>
                        Nearest Safe Period
                      </InputLabel>
                      <Select
                        labelId="pf-safe-period-label"
                        label="Nearest Safe Period"
                        value={demandWindowDays ?? ""}
                        displayEmpty
                        notched
                        renderValue={(v) => (
                          <Box
                            component="span"
                            sx={{ display: "flex", gap: 0.75, minWidth: 0 }}
                          >
                            <span>
                              {v === "" || v == null ? "Off" : `${v} days`}
                            </span>
                            <Box
                              component="span"
                              sx={{
                                fontSize: 12,
                                color: colors.textSecondary,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {v === "" || v == null
                                ? "· average the whole search window"
                                : "· closest run in stock"}
                            </Box>
                          </Box>
                        )}
                        onChange={(e) =>
                          setDemandWindowDays(
                            e.target.value === ""
                              ? null
                              : Number(e.target.value),
                          )
                        }
                      >
                        <MenuItem value="">Off</MenuItem>
                        {LOST_SALES_SAFE_PERIOD_OPTIONS.map((days) => (
                          <MenuItem key={days} value={days}>
                            {days} days
                          </MenuItem>
                        ))}
                      </Select>
                      {(invalidDemandWindow || demandExceedsSearch) && (
                        <FormHelperText>
                          {invalidDemandWindow
                            ? "Choose Off or a positive whole number of days."
                            : "Must not be longer than the search window."}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      border: `1px solid ${colors.border}`,
                      borderRadius: "8px",
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: colors.textPrimary,
                        }}
                      >
                        Day-of-week adjustment
                      </Typography>
                      <Typography
                        sx={{ fontSize: 12, color: colors.textSecondary }}
                      >
                        Compare a Saturday with past Saturdays, not the overall
                        daily average.
                      </Typography>
                    </Box>
                    <Switch
                      size="small"
                      checked={dowAdjustmentEnabled}
                      onChange={(e) =>
                        setDowAdjustmentEnabled(e.target.checked)
                      }
                      disabled={submitting}
                      inputProps={{ "aria-label": "Day-of-week adjustment" }}
                      sx={{ ml: "auto" }}
                    />
                  </Box>
                </Box>
              </Collapse>
            </Box>

            {/* Sales Fill + Promo / Discount, side by side */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: isUpdatedEngine ? "1fr 1fr" : "1fr" },
                gap: 1.75,
                // Both cards the same height.
                alignItems: "stretch",
              }}
            >
              {/* Sales Fill (Updated Forecast) */}
              {isUpdatedEngine && (
                <Box
                  sx={{
                    ...blockSx,
                    height: "100%",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    borderColor: salesFill ? ui.accent : colors.border,
                    transition: "border-color 0.2s",
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 13, color: colors.textPrimary }}>
                      Sales Fill
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: colors.textSecondary, mt: 0.25 }}>
                      For a SKU that started selling after {historyFrom ? monthLabel(historyFrom) : "Sales History From"}: estimates the
                      months before its first sale from the product's other SKUs, or from its category when the product is new too. A new
                      launch with little or no sales of its own is sized as an average SKU of them. The estimated months are marked on the
                      result.
                      {salesFillLocked && " Set by the DropSkip team; it can't be changed here."}
                    </Typography>
                  </Box>
                  <Switch
                    checked={salesFill && !salesFillLocked}
                    onChange={(e) => setSalesFill(e.target.checked)}
                    disabled={submitting || salesFillLocked}
                    inputProps={{ "aria-label": "Sales Fill" }}
                    sx={{ mr: -0.75 }}
                  />
                </Box>
              )}

              {/* Promo / Discount — same card shape as Sales Fill: switch on the
                  right; when on, the discount fields replace the description,
                  so the card keeps its height. */}
              <Box
                sx={{
                  ...blockSx,
                  height: "100%",
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  borderColor: !discountCollapsed ? ui.accent : colors.border,
                  transition: "border-color 0.2s",
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 13, color: colors.textPrimary }}>
                    Promo / Campaign Discount
                  </Typography>
                  {discountCollapsed ? (
                    <Typography sx={{ fontSize: 12, color: colors.textSecondary, mt: 0.25 }}>
                      Applies a planned promo or campaign discount to the forecast periods, so the forecast reflects it.
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", gap: 1, mt: 0.75 }}>
                      <FormControl size="small" disabled={submitting} sx={{ flex: 1, minWidth: 0 }}>
                        <InputLabel id="pf-discount-label">Select Discount</InputLabel>
                        <Select
                          labelId="pf-discount-label"
                          label="Select Discount"
                          value={discountRange}
                          onChange={(e) => {
                            const v = e.target.value;
                            setDiscountRange(v);
                            if (v !== "custom") setDiscountValue(Number(v) || 0);
                            else setDiscountValue("");
                          }}
                          style={{ backgroundColor: "var(--bg-paper)" }}
                          MenuProps={{ PaperProps: { sx: { maxHeight: 260 } } }}
                        >
                          {Array.from({ length: 10 }, (_, i) => (
                            <MenuItem key={i} value={`${(i + 1) * 5}`}>
                              {(i + 1) * 5}%
                            </MenuItem>
                          ))}
                          <MenuItem value="custom">Custom</MenuItem>
                        </Select>
                      </FormControl>
                      {discountRange === "custom" && (
                        <NumberField
                          value={discountValue}
                          onChange={(val) => setDiscountValue(val === "" ? "" : Math.max(0, Number(val)))}
                          inputProps={{ min: 0, "aria-label": "Custom discount %" }}
                          size="small"
                          disabled={submitting}
                          placeholder="Discount"
                          sx={{ width: 110, flexShrink: 0 }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end" sx={{ fontWeight: 600 }}>
                                %
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
                <Switch
                  checked={!discountCollapsed}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setDiscountCollapsed(!on);
                    // Off means no discount: clear it so none is sent.
                    if (!on) {
                      setDiscountRange("");
                      setDiscountValue("");
                    }
                  }}
                  disabled={submitting}
                  inputProps={{ "aria-label": "Promo / Campaign Discount" }}
                  sx={{ mr: -0.75 }}
                />
              </Box>
            </Box>

            {/* Event Uplift (Updated Forecast) — same card shape as Sales Fill /
                Promo: switch and "Add event" on the right; the forecast dates,
                the events and their notes all inside the card. */}
            {isUpdatedEngine && (
              <Box
                sx={{
                  ...blockSx,
                  borderColor: !upliftCollapsed ? ui.accent : colors.border,
                  transition: "border-color 0.2s",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 13, color: colors.textPrimary }}>
                      Event Uplift
                      {eventUplifts.length > 0 && (
                        <Box
                          component="span"
                          sx={{
                            ml: 1,
                            fontSize: 11,
                            fontWeight: 700,
                            color: ui.accent,
                            backgroundColor: ui.accentSoft,
                            borderRadius: "20px",
                            px: 1,
                            py: 0.2,
                          }}
                        >
                          {eventUplifts.length}
                        </Box>
                      )}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: colors.textSecondary, mt: 0.25 }}>
                      Expected extra sales on days the history can't show (e.g. no sales last Black Friday). Added on
                      top of the forecast and shown separately.
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setUpliftCollapsed(false);
                      setEventUplifts((rows) => [...rows, newUpliftRow()]);
                    }}
                    disabled={submitting || eventUplifts.length >= 20}
                    sx={{ textTransform: "none", flexShrink: 0, whiteSpace: "nowrap" }}
                  >
                    Add event
                  </Button>
                  <Switch
                    checked={!upliftCollapsed}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setUpliftCollapsed(!on);
                      // On starts with one event to fill in; off means no uplift.
                      if (on) setEventUplifts((rows) => (rows.length ? rows : [newUpliftRow()]));
                      else setEventUplifts([]);
                    }}
                    disabled={submitting}
                    inputProps={{ "aria-label": "Event Uplift" }}
                    sx={{ mr: -0.75 }}
                  />
                </Box>
                {!upliftCollapsed && (
                  <Box
                    sx={{
                      mt: 1.5,
                      pt: 1.5,
                      borderTop: `1px solid ${colors.border}`,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                    }}
                  >
                    <Typography sx={{ fontSize: 12, color: colors.textSecondary, textAlign: "left" }}>
                      This forecast covers {shortDay(window_.start)} – {shortDay(window_.end)}. Events outside these
                      dates add nothing.
                    </Typography>
                    {eventUplifts.map((r) => (
                      <Box key={r.id} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Box
                        sx={{
                          display: "grid",
                          // Custom event: its name box sits on the same line, after the event.
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm:
                              r.preset === "custom"
                                ? "1.3fr 1.3fr 1fr 1fr 0.8fr auto"
                                : "1.6fr 1fr 1fr 0.8fr auto",
                          },
                          gap: 1.25,
                          alignItems: "start",
                        }}
                      >
                        <TextField
                          select
                          size="small"
                          fullWidth
                          label="Event"
                          value={r.preset}
                          onChange={(e) => {
                            const key = e.target.value;
                            updateUplift(
                              r.id,
                              key === "custom"
                                ? // Own dates, so a preset's dates don't carry over unnoticed.
                                  { preset: key, name: "", start: "", end: "" }
                                : { preset: key, ...presetDates(key) },
                            );
                          }}
                          disabled={submitting}
                          sx={fieldSx}
                        >
                          {EVENT_PRESETS.map((p) => (
                            <MenuItem key={p.key} value={p.key}>
                              {p.name}
                            </MenuItem>
                          ))}
                          <MenuItem value="custom">Custom event</MenuItem>
                        </TextField>
                        {r.preset === "custom" && (
                          <TextField
                            size="small"
                            fullWidth
                            label="Event name"
                            placeholder="e.g. TikTok live sale"
                            value={r.name}
                            onChange={(e) => updateUplift(r.id, { name: e.target.value })}
                            inputProps={{ maxLength: 100 }}
                            disabled={submitting}
                            sx={fieldSx}
                          />
                        )}
                        <DatePickerField
                          label="From"
                          value={r.start}
                          onChange={(v) =>
                            updateUplift(r.id, { start: v, ...(r.end && r.end < v ? { end: v } : {}) })
                          }
                          disabled={submitting}
                          testId="uplift-start"
                        />
                        <DatePickerField
                          label="To"
                          value={r.end}
                          min={r.start}
                          onChange={(v) => updateUplift(r.id, { end: v })}
                          disabled={submitting}
                          testId="uplift-end"
                        />
                        <TextField
                          size="small"
                          fullWidth
                          type="number"
                          label="Lift"
                          placeholder="e.g. 80"
                          value={r.lift_pct}
                          onChange={(e) => updateUplift(r.id, { lift_pct: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                          inputProps={{ min: -90, max: 1000, step: 5, "data-testid": "uplift-lift" }}
                          disabled={submitting}
                          sx={fieldSx}
                        />
                        <IconButton
                          aria-label={`Remove ${r.name || "event"}`}
                          onClick={() => setEventUplifts((rows) => rows.filter((x) => x.id !== r.id))}
                          disabled={submitting}
                          sx={{ mt: 0.25 }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      {upliftOutside(r) && (
                        <Typography
                          data-testid="uplift-outside"
                          sx={{ fontSize: 12, color: ui.warning, textAlign: "left" }}
                        >
                          {`${r.name || "This event"} (${shortDay(r.start)}${
                            r.end && r.end !== r.start ? ` – ${shortDay(r.end)}` : ""
                          }) is outside the forecast dates, so it won't change the forecast. Check the year.`}
                        </Typography>
                      )}
                      </Box>
                    ))}
                    {upliftProblem && (
                      <Typography sx={{ fontSize: 12, color: ui.danger, textAlign: "left" }}>{upliftProblem}</Typography>
                    )}
                    {eventUplifts.length === 0 && (
                      <Typography sx={{ fontSize: 12, color: colors.textSecondary, textAlign: "left" }}>
                        No events yet. Use Add event to add one.
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: 12, color: colors.textSecondary, textAlign: "left" }}>
                      The forecast's tested accuracy doesn't include the uplift.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <Collapse in={paramsOpen} unmountOnExit>
            <Box sx={{ ...cardGridSx, mt: 0.5 }}>
              {!isAllSkusView && (
                <StatCard label="SKU" value={run?.sku || input.sku} />
              )}
              <StatCard
                label="Forecast By"
                value={input.forecast_by || "SKU"}
              />
              {pName && <StatCard label="Product" value={pName} />}
              <StatCard label="Time Bucket" value={input.type} />
              <StatCard label="Periods" value={num(input.horizon)} />
              <StatCard
                label="Discount"
                value={input.discount ? `${num(input.discount)}%` : "None"}
              />
              {input.category && (
                <StatCard label="Category" value={input.category} />
              )}
              {joinedLabel(input.warehouse, warehouseName) && (
                <StatCard
                  label="Warehouse"
                  value={joinedLabel(input.warehouse, warehouseName)}
                />
              )}
              {joinedLabel(input.channel) && (
                <StatCard label="Channel" value={joinedLabel(input.channel)} />
              )}
              {input.lead_time_days != null && (
                <StatCard
                  label="Lead Time (days)"
                  value={num(input.lead_time_days)}
                />
              )}
              {input.safety_stock_percentage != null && (
                <StatCard
                  label="Safety Stock (% of on hand)"
                  value={`${num(input.safety_stock_percentage)}%`}
                />
              )}
              {/* Only runs made after these parameters existed carry them. */}
              {input.min_coverage_days != null && (
                <StatCard
                  label="Minimum Coverage (days)"
                  value={num(input.min_coverage_days)}
                />
              )}
              {input.min_coverage_days != null && (
                <StatCard
                  label="Min Order Qty (per SKU)"
                  value={
                    input.min_order_qty != null
                      ? num(input.min_order_qty)
                      : "SKU default"
                  }
                />
              )}
              {Array.isArray(input.event_uplifts) && input.event_uplifts.length > 0 && (
                <StatCard
                  label="Event Uplift"
                  value={input.event_uplifts
                    .map((e) => `${e.name} ${e.lift_pct > 0 ? "+" : ""}${num(e.lift_pct)}%`)
                    .join(", ")}
                />
              )}
              {input.history_start && (
                <StatCard
                  label="Sales History From"
                  value={new Date(input.history_start).toLocaleDateString(
                    undefined,
                    { month: "short", year: "numeric", timeZone: "UTC" },
                  )}
                />
              )}
              {input.sales_fill && (
                <StatCard
                  label="Sales Fill"
                  value={(() => {
                    const fill = run?.forecast_model?.sales_fill;
                    if (fill?.applied) return `On · ${num(fill.periods)} ${fill.unit || "period"}${fill.periods === 1 ? "" : "s"} estimated`;
                    if (fill?.requested) return "On · nothing filled";
                    return "On";
                  })()}
                />
              )}
              {input.include_lost_sales && (
                <StatCard
                  label="Lost Sales"
                  value={`On · ${num(input.search_window_days)}-day search${
                    input.demand_window_days
                      ? ` · ${num(input.demand_window_days)}-day safe period`
                      : ""
                  }${input.dow_adjustment_enabled ? " · DOW" : ""}`}
                />
              )}
            </Box>
            {extra && (
              <Box
                sx={{
                  mt: 2.25,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {extra}
              </Box>
            )}
          </Collapse>
        )}
      </Box>

      {/* Per-SKU forecast results — shown when a run had any failures */}
      <Dialog
        open={resultDialog.open}
        onClose={() => setResultDialog((p) => ({ ...p, open: false }))}
        PaperProps={{ sx: { width: { xs: "92vw", sm: 520 }, borderRadius: 2 } }}
      >
        <DialogTitle>Forecast Results</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}
        >
          {okCount > 0 && failCount > 0 && (
            <Box sx={{ display: "flex", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
              <Chip
                size="small"
                icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                label={`${okCount} succeeded`}
                sx={{
                  backgroundColor: "#DCFCE7",
                  color: "#166534",
                  "& .MuiChip-icon": { color: "#16A34A" },
                }}
              />
              <Chip
                size="small"
                icon={<ErrorOutlineIcon sx={{ fontSize: 14 }} />}
                label={`${failCount} failed`}
                sx={{
                  backgroundColor: "#FEE2E2",
                  color: "#991B1B",
                  "& .MuiChip-icon": { color: "#DC2626" },
                }}
              />
            </Box>
          )}
          {results.map((item, i) => {
            const sku = item.sku || item.data?.sku;
            return item.error ? (
              <Box
                key={`err-${sku || i}`}
                sx={{
                  p: 2,
                  border: "1px solid #FCA5A5",
                  borderRadius: "12px",
                  backgroundColor: "#FEF2F2",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                }}
              >
                <ErrorOutlineIcon
                  sx={{
                    color: "#DC2626",
                    flexShrink: 0,
                    mt: "2px",
                    fontSize: 18,
                  }}
                />
                <Box>
                  {sku && (
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#991B1B",
                        mb: 0.3,
                      }}
                    >
                      SKU: {sku}
                    </Typography>
                  )}
                  <Typography
                    sx={{ fontSize: 12, color: "#DC2626", lineHeight: 1.4 }}
                  >
                    {item.message || "Forecast failed for this SKU"}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box
                key={`ok-${sku || i}`}
                sx={{
                  p: 2,
                  border: "1px solid #BBF7D0",
                  borderRadius: "12px",
                  backgroundColor: "#F0FDF4",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                }}
              >
                <CheckCircleIcon
                  sx={{
                    color: "#16A34A",
                    flexShrink: 0,
                    mt: "2px",
                    fontSize: 18,
                  }}
                />
                <Box>
                  {sku && (
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#166534",
                        mb: 0.3,
                      }}
                    >
                      SKU: {sku}
                    </Typography>
                  )}
                  <Typography
                    sx={{ fontSize: 12, color: "#15803D", lineHeight: 1.4 }}
                  >
                    {item.message || "Forecast generated"}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setResultDialog((p) => ({ ...p, open: false }))}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default ForecastParamsPanel;
